import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { Dialog, DialogContent } from '@mui/material'
import { createStyles, makeStyles } from '@mui/styles'
import CloseIcon from '@mui/icons-material/Close'
// import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'

import { Logger, LoggerTypes } from '../../../utils'
import { dialogSize, userRightTypes } from '../../../constants'
import { AccessControl, AccessControlNoRights, Button, buttonModes, buttonTypes } from '../../../components'
import { GlobalAttributeTabs } from '../GlobalAttributeTabs'
import './style.scss'

const useStyles = makeStyles((theme) =>
  createStyles({
    closeStyle: {
      border: `1px solid ${theme.palette.primary.main}`
    },
    closeIconStyle: {
      color: theme.palette.primary.main
    }
  })
)

/**
 * Display the Global attributes popin that allows to import in either `project` or `scenario`
 * Somme attributes.
 *
 * @param {object} currentProject,
 * @param {object} currentScenario,
 * @param {number} tabValue,
 * @param {func} setTabValue,
 * @param {array<string>} tabIdentifier,
 * @param {boolean} openDialog,
 * @param {func} setOpenDialog,
 * @param {func} actionDispatchImport,
 * @param {object} tabVisible Define the tab to display.
 * @returns {Dialog}
 */
export const GlobalAttributeTabsDialog = ({
  currentProject,
  currentScenario,
  tabValue,
  setTabValue,
  tabIdentifier,
  openDialog,
  setOpenDialog,
  actionDispatchImport,
  tabVisible,
  selectedAttributes,
  loadData,
  isMargin
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const styleClasses = useStyles()

  const globalResources = useSelector((state) => state.globalResources) || {}
  const globalVariables = useSelector((state) => state.globalVariables) || {}
  const globalProducts = useSelector((state) => state.globalProducts) || {}
  const globalLocations = useSelector((state) => state.globalLocations) || {}
  const globalWeathers = useSelector((state) => state.globalWeathers) || {}
  const globalParameters = useSelector((state) => state.globalParameters) || {}

  const tabName = tabIdentifier[tabValue]

  const onDialogClose = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'GlobalAttributeTabsDialog.onDialogClose()')
    setOpenDialog(false)
  }

  /**
   * Function to call on each selected row in global attribute Datagrid.
   *
   * @param {object} importGlobal Array of Ex: { weather_data: ['uuid', 'uuid' ]}
   * @returns {Promise<object>}
   */
  const onGlobalsImport = (importGlobal) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'GlobalAttributeTabsDialog.onGlobalsImport()', {
      currentProject,
      importGlobal
    })

    return dispatch(actionDispatchImport(importGlobal, currentProject?.id, currentScenario?.id))
      .then((res) => {
        Logger.trace(
          LoggerTypes.CONTROLLER,
          'GlobalAttributeTabsDialog.onGlobalsImport().dispatch(actionDispatchImport(...)).then()',
          { res }
        )

        if (res?.data?.isAxiosError) {
          enqueueSnackbar(t('common.api_failure'), { variant: 'error' })
        } else {
          // -- load the data
          const currentTab = tabIdentifier[tabValue]
          const currentFresher = loadData.find((x) => x.id === currentTab)

          Logger.trace(
            LoggerTypes.CONTROLLER,
            'GlobalAttributeTabsDialog.onGlobalsImport().dispatch(actionDispatchImport(...)).then()._success()',
            { currentTab, loadData, currentFresher }
          )

          if (currentFresher && currentFresher.dispatch) {
            dispatch(currentFresher.dispatch)
          }
        }
      })
      .catch((res) => {
        Logger.trace(
          LoggerTypes.WARN,
          'GlobalAttributeTabsDialog.onGlobalsImport().dispatch(actionDispatchImport(...)).catch()',
          { res }
        )
      })
  }

  Logger.trace(LoggerTypes.CONTAINER, 'GlobalAttributeTabsDialog.render()', {
    currentProject,
    currentScenario,
    tabValue,
    tabName,
    tabIdentifier,
    openDialog,
    tabVisible,
    selectedAttributes
  })

  return (
    openDialog && (
      <Dialog
        fullWidth={true}
        maxWidth={dialogSize.LARGE}
        open={openDialog}
        // onClose={onDialogClose} // <-- allow to close the popin while clicking on background
        aria-labelledby="form-dialog-title"
        className={`GlobalAttributeTabsDialog ${!isMargin ? 'no-margin' : ''}`}
      >
        <DialogContent>
          <Button
            mode={buttonModes.SECONDARY_ACTION}
            type={buttonTypes.BUTTON}
            tooltip={t('common.close')}
            icon={<CloseIcon className={styleClasses.closeIconStyle} />}
            onClick={onDialogClose}
            className={`headerButton ${styleClasses.closeStyle}`}
          />
          <AccessControl
            userPermissions={[userRightTypes.GLOBAL_ATTRIBUTES_READ]}
            renderNoAccess={() => <AccessControlNoRights />}
          >
            <GlobalAttributeTabs
              currentProject={currentProject}
              currentScenario={currentScenario}
              globalResources={globalResources}
              globalVariables={globalVariables}
              globalProducts={globalProducts}
              globalLocations={globalLocations}
              globalWeathers={globalWeathers}
              globalParameters={globalParameters}
              tabName={tabName}
              setTabValue={setTabValue}
              tabVisible={tabVisible}
              selectedAttributes={selectedAttributes}
              onGlobalsImport={onGlobalsImport}
            />
          </AccessControl>
        </DialogContent>
      </Dialog>
    )
  )
}

GlobalAttributeTabsDialog.displayName = 'GlobalAttributeTabsDialog'

GlobalAttributeTabsDialog.propTypes = {
  currentProject: PropTypes.object,
  currentScenario: PropTypes.object,
  tabValue: PropTypes.number,
  tabIdentifier: PropTypes.arrayOf(PropTypes.string),
  actionDispatchImport: PropTypes.func.isRequired,
  openDialog: PropTypes.bool.isRequired,
  setOpenDialog: PropTypes.func.isRequired,
  setTabValue: PropTypes.func.isRequired,
  tabVisible: PropTypes.shape({
    variables: PropTypes.bool,
    products: PropTypes.bool,
    resources: PropTypes.bool,
    locations: PropTypes.bool,
    weather_data: PropTypes.bool,
    parameters: PropTypes.bool
  }),
  selectedAttributes: PropTypes.shape({
    variables: PropTypes.arrayOf(PropTypes.object),
    products: PropTypes.arrayOf(PropTypes.object),
    resources: PropTypes.arrayOf(PropTypes.object),
    locations: PropTypes.arrayOf(PropTypes.object),
    weather_data: PropTypes.arrayOf(PropTypes.object),
    parameters: PropTypes.arrayOf(PropTypes.object)
  }),
  loadData: PropTypes.arrayOf(PropTypes.object),
  isMargin: PropTypes.bool
}

GlobalAttributeTabsDialog.defaultProps = {
  currentProject: null,
  currentScenario: null,
  tabValue: null,
  tabIdentifier: ['weather_data', 'locations', 'resources', 'products', 'variables', 'parameters'],
  actionDispatchImport: () => {},
  openDialog: false,
  setOpenDialog: () => {},
  setTabValue: () => {},
  tabVisible: {
    variables: true,
    products: true,
    resources: true,
    locations: true,
    weather_data: true,
    parameters: true
  },
  selectedAttributes: null,
  loadData: [],
  isMargin: true
}
